import axios from "axios";
import {
  CustomListResponse,
  AudienceSummaryResponse,
  AudienceTagsResponse,
  PreviewListResponse,
  BulkImportListResponse,
  CustomerGroupsResponse,
  DeleteCustomListResponse,
  UpdateCustomListStatusResponse,
} from "src/layouts/Engagements/EngagementInterfaces";
import { getApiBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import { getApiKey, getTenantId, getUserId } from "src/utils/getters";

const API_BASE_URL = getApiBaseUrl();

// ========== Get Total Lists API ==========
export const getCustomLists = async (): Promise<CustomListResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const params = new URLSearchParams({
    tenant_id: String(tenantId),
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const url = `${API_BASE_URL}/engagements/view-custom-lists?${params.toString()}`;

  const response = await axios.post<CustomListResponse>(
    url,
    {},
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

const postTenantAudienceEndpoint = async <T,>(path: string, lang: string): Promise<T> => {
  const params = new URLSearchParams({
    tenant_id: String(getTenantId()),
    lang: normalizeApiLang(lang),
  });
  const response = await axios.post<T>(
    `${API_BASE_URL}/engagements/audience/${path}?${params.toString()}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== Audience Summary API ==========
export const getAudienceSummary = (lang: string): Promise<AudienceSummaryResponse> =>
  postTenantAudienceEndpoint<AudienceSummaryResponse>("audience-summary", lang);

// ========== Customer Groups API ==========
export const getCustomerGroups = (lang: string): Promise<CustomerGroupsResponse> =>
  postTenantAudienceEndpoint<CustomerGroupsResponse>("customer-groups", lang);

// ========== Audience Tags API ==========
let pendingAudienceTagsRequest: Promise<AudienceTagsResponse> | null = null;

export const getAudienceTags = (): Promise<AudienceTagsResponse> => {
  if (!pendingAudienceTagsRequest) {
    pendingAudienceTagsRequest = fetchAudienceTags().finally(() => {
      pendingAudienceTagsRequest = null;
    });
  }
  return pendingAudienceTagsRequest;
};

const fetchAudienceTags = async (): Promise<AudienceTagsResponse> => {
  const response = await axios.post<AudienceTagsResponse>(
    `${API_BASE_URL}/engagements/audience/tags`,
    {
      tenant_id: Number(getTenantId()),
      user_id: Number(getUserId()),
    },
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== Preview List API ==========
export const previewListAPI = async (formData: FormData): Promise<PreviewListResponse> => {
  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.post<PreviewListResponse>(
    `${API_BASE_URL}/engagements/audience/preview-list?${params.toString()}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
      },
    }
  );

  return response.data;
};

// ========== MODIFY LIST API ==========
export const uploadModifiedCustomList = async (
  customListId: number,
  file: File
): Promise<void> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  formData.append("custom_list_id", customListId.toString());

  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await fetch(
    `${API_BASE_URL}/engagements/update-custom-list?${params.toString()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error("Failed to save changes");
  }
};

// ========== BULK IMPORT LIST API ==========
export const bulkImportCustomList = async (
  customListId: number,
  file: File
): Promise<BulkImportListResponse> => {
  const formData = new FormData();
  formData.append("tenant_id", String(getTenantId()));
  formData.append("custom_list_id", String(customListId));
  formData.append("file", file);

  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.post<BulkImportListResponse>(
    `${API_BASE_URL}/engagements/audience/bulk-import-list?${params.toString()}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
      },
    }
  );

  return response.data;
};

// ========== GET CSV CONTENT FOR  API ==========
export const getCustomListCSV = async (listId: number): Promise<Blob> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await fetch(`${API_BASE_URL}/engagements/edit-custom-list?${params.toString()}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      tenant_id: tenantId,
      custom_list_id: listId,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch CSV for custom list");
  }

  return await response.blob();
};

// ========== DELETE LIST API ==========
export const deleteCustomList = async (
  selectedListId: number
): Promise<DeleteCustomListResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const payload = {
    tenant_id: tenantId,
    custom_list_ids: [selectedListId],
  };

  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.post<DeleteCustomListResponse>(
    `${API_BASE_URL}/engagements/delete-custom-list?${params.toString()}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

export const updateCustomListStatus = async (
  selectedListId: number,
  actionType: "archive" | "active"
): Promise<UpdateCustomListStatusResponse> => {
  const apiKey = getApiKey();
  const tenantId = getTenantId();

  const payload = {
    tenant_id: tenantId,
    custom_list_ids: [selectedListId],
    status: actionType,
  };

  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.put<UpdateCustomListStatusResponse>(
    `${API_BASE_URL}/engagements/update-custom-list-status?${params.toString()}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
    }
  );

  return response.data;
};

// ========== Bulk update==========
export const bulkDeleteCustomLists = async (
  tenant_id: string,
  custom_list_ids: number[]
) => {
  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.post(
    `${API_BASE_URL}/engagements/delete-custom-list?${params.toString()}`,
    {
      tenant_id,
      custom_list_ids,
    },
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const bulkUpdateCustomListStatus = async (
  tenant_id: string,
  custom_list_ids: number[],
  status: "archive" | "active"
) => {
  const params = new URLSearchParams({
    lang: normalizeApiLang(getApiLangFromI18n(i18n)),
  });
  const response = await axios.put(
    `${API_BASE_URL}/engagements/update-custom-list-status?${params.toString()}`,
    {
      tenant_id,
      custom_list_ids,
      status,
    },
    {
      headers: {
        Authorization: `Bearer ${getApiKey()}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
